/**
 * Tenant-local calendar dates.
 *
 * Punches are instants and most stored date strings are derived in UTC, but a
 * pay period is a run of *civil* dates in the tenant's own timezone: a period
 * ending "2026-07-15" ends when that day ends where the employees work, not at
 * 2026-07-15T00:00Z.
 *
 * Conflating the two is what made the nightly auto-submit job freeze every
 * timesheet a day early. It ran at 00:00 UTC and asked whether the period
 * ended on `new Date().toISOString().split('T')[0]`, which on the period's own
 * last day is true — so it submitted before anyone had punched that day and
 * the last day's hours were never counted. 14 of 33 prod timesheets in a
 * three-month window were short by 4.2h to 10.4h each. Nothing had been paid
 * from those records yet; the job that consumes them had never run.
 *
 * So: anything that decides whether a civil day is *over* goes through here,
 * with the tenant's IANA timezone. Instants stay instants.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
/**
 * Whether the runtime can resolve this IANA timezone name.
 *
 * Tenant records carry whatever string was stored at signup, so callers
 * validate before trusting it — `tenantLocalDate` throws on an unknown zone,
 * and a payroll job must not die because one tenant has a typo in its
 * settings.
 */
export function isSupportedTimeZone(timeZone) {
    if (typeof timeZone !== 'string' || timeZone.length === 0)
        return false;
    try {
        // Constructing the formatter is the check; an unknown zone throws RangeError.
        new Intl.DateTimeFormat('en-US', { timeZone });
        return true;
    }
    catch {
        return false;
    }
}
/**
 * The calendar date (YYYY-MM-DD) that `instant` falls on in `timeZone`.
 *
 * Throws RangeError for a timezone the runtime does not know — call
 * `isSupportedTimeZone` first when the value came from stored data.
 */
export function tenantLocalDate(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(instant);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;
    if (!year || !month || !day) {
        throw new RangeError(`Could not derive a calendar date in timezone ${timeZone}`);
    }
    return `${year}-${month}-${day}`;
}
/**
 * Move a YYYY-MM-DD date by whole days, staying on the calendar.
 *
 * Pure date arithmetic: no timezone, no DST, no clock. "The day before
 * 2026-03-09" is 2026-03-08 whether or not that night lost an hour.
 */
export function shiftIsoDate(date, days) {
    if (!ISO_DATE.test(date)) {
        throw new RangeError(`Expected a YYYY-MM-DD date, got: ${date}`);
    }
    const year = Number(date.slice(0, 4));
    const month = Number(date.slice(5, 7));
    const day = Number(date.slice(8, 10));
    const shifted = new Date(Date.UTC(year, month - 1, day) + days * MS_PER_DAY);
    return shifted.toISOString().slice(0, 10);
}
